import { useCallback, useEffect, useRef } from "react";

import { type FileRejection, useDropzone } from "react-dropzone";

import { toastError } from "@alpacahq/ui/old";

import {
  fileToDataUrl,
  LOGO_MAX_SIZE_BYTES,
  MAX_SCREENSHOTS,
  SCREENSHOT_MAX_SIZE_BYTES,
  validateLogoFile,
  validateScreenshotFile,
} from "../SubmitApp.utils";

export interface GraphicsFieldsChangeOptions {
  shouldDirty: true;
  shouldValidate: true;
}

export type GraphicsFieldsChangeHandler<T> = (
  value: T,
  options: GraphicsFieldsChangeOptions,
) => void;

export interface UseGraphicsFieldsControllerOptions {
  logo: string;
  screenshots: string[];
  screenshotError?: string;
  onLogoChange: GraphicsFieldsChangeHandler<string>;
  onScreenshotsChange: GraphicsFieldsChangeHandler<string[]>;
}

const CHANGE_OPTIONS: GraphicsFieldsChangeOptions = {
  shouldDirty: true,
  shouldValidate: true,
};

const getRejectedFileMessage = (
  rejection: FileRejection,
  validate: (file: File) => string | null,
) =>
  validate(rejection.file) ??
  rejection.errors[0]?.message ??
  "The selected file could not be uploaded.";

export const useGraphicsFieldsController = ({
  logo,
  screenshots,
  screenshotError,
  onLogoChange,
  onScreenshotsChange,
}: UseGraphicsFieldsControllerOptions) => {
  const isActive = useRef(true);
  const logoReadGeneration = useRef(0);
  const screenshotsRef = useRef(screenshots);
  screenshotsRef.current = screenshots;

  useEffect(() => {
    isActive.current = true;
    return () => {
      isActive.current = false;
    };
  }, []);

  const changeScreenshots = useCallback(
    (nextScreenshots: string[]) => {
      screenshotsRef.current = nextScreenshots;
      onScreenshotsChange(nextScreenshots, CHANGE_OPTIONS);
    },
    [onScreenshotsChange],
  );

  const changeLogo = useCallback(
    (nextLogo: string) => {
      onLogoChange(nextLogo, CHANGE_OPTIONS);
    },
    [onLogoChange],
  );

  const onLogoDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) {
        return;
      }

      const readGeneration = ++logoReadGeneration.current;
      const validationError = validateLogoFile(file);
      if (validationError) {
        toastError(validationError);
        return;
      }

      try {
        const nextLogo = await fileToDataUrl(file);
        if (
          !isActive.current ||
          readGeneration !== logoReadGeneration.current
        ) {
          return;
        }
        changeLogo(nextLogo);
      } catch (error) {
        if (
          !isActive.current ||
          readGeneration !== logoReadGeneration.current
        ) {
          return;
        }
        toastError(
          error instanceof Error ? error.message : "Failed to read logo file.",
        );
      }
    },
    [changeLogo],
  );

  const onLogoDropRejected = useCallback((rejections: FileRejection[]) => {
    const rejection = rejections[0];
    if (rejection) {
      toastError(getRejectedFileMessage(rejection, validateLogoFile));
    }
  }, []);

  const onScreenshotsDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (
        screenshotsRef.current.length + acceptedFiles.length >
        MAX_SCREENSHOTS
      ) {
        toastError(`Maximum ${MAX_SCREENSHOTS} screenshots permitted`);
        return;
      }

      const validFiles = acceptedFiles.filter((file) => {
        const validationError = validateScreenshotFile(file);
        if (validationError) {
          toastError(validationError);
          return false;
        }
        return true;
      });

      try {
        const nextScreenshots = await Promise.all(
          validFiles.map((file) => fileToDataUrl(file)),
        );
        if (!isActive.current) {
          return;
        }
        const currentScreenshots = screenshotsRef.current;
        const availableSlots = MAX_SCREENSHOTS - currentScreenshots.length;
        if (nextScreenshots.length > availableSlots) {
          toastError(`Maximum ${MAX_SCREENSHOTS} screenshots permitted`);
        }
        if (availableSlots <= 0) {
          return;
        }
        const combinedScreenshots = [
          ...currentScreenshots,
          ...nextScreenshots.slice(0, availableSlots),
        ];
        changeScreenshots(combinedScreenshots);
      } catch (error) {
        if (!isActive.current) {
          return;
        }
        toastError(
          error instanceof Error
            ? error.message
            : "Failed to read screenshot file.",
        );
      }
    },
    [changeScreenshots],
  );

  const onScreenshotsDropRejected = useCallback(
    (rejections: FileRejection[]) => {
      const rejection = rejections[0];
      if (rejection) {
        toastError(getRejectedFileMessage(rejection, validateScreenshotFile));
      }
    },
    [],
  );

  const logoDropzone = useDropzone({
    accept: ["image/gif", "image/jpeg", "image/png"],
    maxFiles: 1,
    maxSize: LOGO_MAX_SIZE_BYTES,
    multiple: false,
    onDropAccepted: onLogoDrop,
    onDropRejected: onLogoDropRejected,
  });

  const screenshotsDropzone = useDropzone({
    accept: ["image/jpeg", "image/png"],
    maxFiles: MAX_SCREENSHOTS,
    maxSize: SCREENSHOT_MAX_SIZE_BYTES,
    multiple: true,
    onDropAccepted: onScreenshotsDrop,
    onDropRejected: onScreenshotsDropRejected,
  });

  return {
    logo,
    screenshots,
    screenshotError,
    logoDropzone: {
      rootProps: logoDropzone.getRootProps({
        "aria-label": "Upload app logo",
      }),
      inputProps: logoDropzone.getInputProps(),
    },
    screenshotsDropzone: {
      rootProps: screenshotsDropzone.getRootProps({
        "aria-label": "Upload app screenshots",
      }),
      inputProps: screenshotsDropzone.getInputProps(),
    },
    onRemoveScreenshot: (index: number) => {
      changeScreenshots(
        screenshotsRef.current.filter(
          (_screenshot, screenshotIndex) => screenshotIndex !== index,
        ),
      );
    },
  };
};
